import React from 'react';
import { Helmet } from 'react-helmet-async';
import blogPosts from './data/blogPosts';

const BlogPost = ({ slug, onBack, onBlogHome }) => {
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return (
      <div style={{ minHeight: '100vh', background: '#0B0B10', padding: '80px 20px', textAlign: 'center' }}>
        <h1 style={{ color: '#FFFFFF' }}>Post not found</h1>
        <button onClick={onBlogHome} style={{ color: '#FF8A3D', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', marginTop: 16 }}>
          ← Back to Blog
        </button>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    const [y, m, d] = dateStr.split('-');
    const months = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[parseInt(m)]} ${parseInt(d)}, ${y}`;
  };

  // Simple markdown-like rendering
  const renderContent = (text) => {
    const lines = text.split('\n');
    const elements = [];
    let listItems = [];

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} style={{ color: '#c0cad8', lineHeight: 1.8, paddingLeft: 24, margin: '12px 0' }}>
            {listItems.map((item, i) => <li key={i}>{renderInline(item)}</li>)}
          </ul>
        );
        listItems = [];
      }
    };

    const renderInline = (text) => {
      // Split on bold and markdown links
      const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
      return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i} style={{ color: '#FFFFFF', fontWeight: 600 }}>{part.slice(2, -2)}</strong>;
        }
        const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (linkMatch) {
          return <a key={i} href={linkMatch[2]} target="_blank" rel="noopener noreferrer" style={{ color: '#FF8A3D', textDecoration: 'underline' }}>{linkMatch[1]}</a>;
        }
        return part;
      });
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      if (line.startsWith('## ')) {
        flushList();
        elements.push(
          <h2 key={i} style={{ color: '#FFFFFF', fontSize: '1.3rem', fontWeight: 600, marginTop: 32, marginBottom: 12 }}>
            {line.slice(3)}
          </h2>
        );
      } else if (line.startsWith('- ')) {
        listItems.push(line.slice(2));
      } else if (line.trim() === '') {
        flushList();
      } else {
        flushList();
        elements.push(
          <p key={i} style={{ color: '#c0cad8', fontSize: '1.05rem', lineHeight: 1.8, margin: '12px 0' }}>
            {renderInline(line)}
          </p>
        );
      }
    }
    flushList();
    return elements;
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    author: { '@type': 'Organization', name: 'Runrora' },
    datePublished: post.date,
    publisher: { '@type': 'Organization', name: 'Runrora', url: 'https://runrora.com' },
    mainEntityOfPage: `https://runrora.com/blog/${post.slug}`,
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0B0B10' }}>
      <Helmet>
        <title>{post.title} — RunRora Blog</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:title" content={`${post.title} — RunRora Blog`} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={post.date} />
        <link rel="canonical" href={`https://runrora.com/blog/${post.slug}`} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '40px 20px' }}>
        <button
          onClick={onBlogHome}
          style={{
            background: 'none', border: 'none', color: '#8b9cb3', cursor: 'pointer',
            fontSize: '0.95rem', marginBottom: 32, display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          ← Blog
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <span style={{
            fontSize: '0.75rem', fontWeight: 600, color: '#FF8A3D',
            backgroundColor: 'rgba(255, 138, 61, 0.15)', padding: '4px 10px', borderRadius: 6,
          }}>
            {post.category}
          </span>
          <span style={{ fontSize: '0.85rem', color: '#5a6a7a' }}>
            {formatDate(post.date)}
          </span>
          <span style={{ fontSize: '0.85rem', color: '#5a6a7a' }}>
            · {post.readTime}
          </span>
        </div>

        <h1 style={{
          color: '#FFFFFF', fontSize: '2.2rem', fontWeight: 700,
          lineHeight: 1.2, marginBottom: 16,
        }}>
          {post.title}
        </h1>

        <p style={{
          color: '#8b9cb3', fontSize: '1.1rem', lineHeight: 1.6,
          marginBottom: 40, fontStyle: 'italic',
        }}>
          {post.excerpt}
        </p>

        <div style={{ borderTop: '1px solid rgba(255, 138, 61, 0.2)', paddingTop: 32 }}>
          {renderContent(post.content)}
        </div>

        <div style={{
          marginTop: 48, padding: 24, background: '#16161D',
          borderRadius: 12, border: '1px solid rgba(255, 138, 61, 0.2)', textAlign: 'center',
        }}>
          <p style={{ color: '#FFFFFF', fontSize: '1.1rem', fontWeight: 600, marginBottom: 8 }}>
            Find your next race on Runrora
          </p>
          <p style={{ color: '#8b9cb3', fontSize: '0.95rem', marginBottom: 16 }}>
            Browse 125+ races, connect with runners, and join race communities.
          </p>
          <button
            onClick={onBack}
            style={{
              backgroundColor: '#FF8A3D', color: '#0B0B10', border: 'none',
              padding: '10px 24px', borderRadius: 8, fontSize: '0.95rem',
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            Explore Races
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
